/**
 * User Engagement Service - Industry Standard Analytics
 * 
 * Provides comprehensive user engagement tracking and analytics including:
 * - Behavioral analytics (page views, session duration, bounce rate)
 * - User journey mapping and funnel analysis
 * - Cohort analysis and retention metrics
 * - Real-time engagement monitoring
 * - Personalization and segmentation
 * - A/B testing support
 * - Performance and conversion tracking
 */

const USER_ENGAGEMENT_KEY = 'user-engagement-v1';
const REAL_TIME_EVENTS_KEY = 'real-time-events-v1';
const USER_SEGMENTS_KEY = 'user-segments-v1';
const AB_TESTS_KEY = 'ab-tests-v1';

// Industry standard engagement metrics
const ENGAGEMENT_THRESHOLDS = {
  session: {
    excellent: 600, // 10+ minutes
    good: 300,      // 5+ minutes
    average: 120,   // 2+ minutes
    poor: 30        // <30 seconds
  },
  pageViews: {
    excellent: 10,
    good: 5,
    average: 3,
    poor: 1
  },
  bounceRate: {
    excellent: 0.25, // <25%
    good: 0.40,      // <40%
    average: 0.55,   // <55%
    poor: 0.70       // >70%
  }
};

// Generate comprehensive engagement data
function generateEngagementData(days = 30) {
  const now = new Date();
  const data = {
    // Behavioral Metrics
    behavioral: [],
    // User Journey Data
    journeys: [],
    // Cohort Analysis
    cohorts: [],
    // Real-time Events
    realTimeEvents: [],
    // User Segments
    segments: [],
    // A/B Tests
    abTests: [],
    // Performance Metrics
    performance: [],
    // Conversion Funnels
    funnels: []
  };

  // Generate behavioral data for each day
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(now.getDate() - i);
    const dateStr = date.toISOString().slice(0, 10);

    // Daily behavioral metrics
    const baseUsers = 800 + Math.floor(Math.random() * 600);
    const sessions = Math.floor(baseUsers * (1.1 + Math.random() * 0.8));
    const pageViews = Math.floor(sessions * (3 + Math.random() * 7));
    const bounceRate = 0.20 + Math.random() * 0.50;
    const avgSessionDuration = 180 + Math.random() * 420; // 3-10 minutes
    const conversionRate = 0.02 + Math.random() * 0.08; // 2-10%
    const engagementScore = calculateEngagementScore(avgSessionDuration, pageViews / sessions, bounceRate);

    data.behavioral.push({
      date: dateStr,
      users: baseUsers,
      sessions,
      pageViews,
      bounceRate: Number(bounceRate.toFixed(3)),
      avgSessionDuration: Number(avgSessionDuration.toFixed(1)),
      conversionRate: Number(conversionRate.toFixed(3)),
      engagementScore: Number(engagementScore.toFixed(2)),
      newUsers: Math.floor(baseUsers * (0.15 + Math.random() * 0.25)),
      returningUsers: Math.floor(baseUsers * (0.60 + Math.random() * 0.25))
    });
  }

  // Generate user journey data
  const journeyTemplates = [
    { name: 'Landing → Dashboard → Report', conversion: 0.45 },
    { name: 'Search → Results → Detail → Action', conversion: 0.35 },
    { name: 'Login → Navigation → Feature Use', conversion: 0.65 },
    { name: 'Dashboard → Settings → Configuration', conversion: 0.25 },
    { name: 'Analytics → Export → Download', conversion: 0.55 }
  ];

  journeyTemplates.forEach((template, index) => {
    const sessions = 100 + Math.floor(Math.random() * 300);
    data.journeys.push({
      id: `journey_${index + 1}`,
      name: template.name,
      sessions,
      completions: Math.floor(sessions * template.conversion),
      conversionRate: template.conversion,
      avgDuration: 300 + Math.random() * 600,
      dropoffPoints: generateDropoffPoints(),
      topPages: generateTopPages()
    });
  });

  // Generate cohort data
  for (let week = 12; week >= 0; week--) {
    const cohortDate = new Date(now);
    cohortDate.setDate(now.getDate() - (week * 7));
    const cohortSize = 50 + Math.floor(Math.random() * 150);
    
    data.cohorts.push({
      cohort: cohortDate.toISOString().slice(0, 10),
      size: cohortSize,
      week0: 1.0,
      week1: 0.65 + Math.random() * 0.20,
      week2: 0.45 + Math.random() * 0.25,
      week4: 0.30 + Math.random() * 0.20,
      week8: 0.20 + Math.random() * 0.15,
      week12: 0.15 + Math.random() * 0.10
    });
  }

  // Generate user segments
  data.segments = [
    { name: 'Power Users', size: 145, engagement: 'High', growth: '+12%', color: '#28a745' },
    { name: 'Regular Users', size: 620, engagement: 'Medium', growth: '+5%', color: '#17a2b8' },
    { name: 'New Users', size: 180, engagement: 'Low', growth: '+25%', color: '#ffc107' },
    { name: 'At Risk', size: 85, engagement: 'Low', growth: '-8%', color: '#dc3545' },
    { name: 'Mobile First', size: 290, engagement: 'Medium', growth: '+18%', color: '#6f42c1' }
  ];

  // Generate A/B tests
  data.abTests = [
    {
      id: 'test_001',
      name: 'Dashboard Layout Optimization',
      status: 'Running',
      variants: [
        { name: 'Control', traffic: 50, conversions: 145, visitors: 1250 },
        { name: 'Variant A', traffic: 50, conversions: 168, visitors: 1280 }
      ],
      significance: 0.85,
      lift: '+15.8%'
    },
    {
      id: 'test_002',
      name: 'CTA Button Color Test',
      status: 'Completed',
      variants: [
        { name: 'Blue', traffic: 33, conversions: 89, visitors: 890 },
        { name: 'Green', traffic: 33, conversions: 112, visitors: 920 },
        { name: 'Orange', traffic: 34, conversions: 98, visitors: 885 }
      ],
      significance: 0.95,
      lift: '+25.8%'
    }
  ];

  // Generate performance metrics
  data.performance = [
    { metric: 'Page Load Time', value: 2.3, target: 3.0, trend: 'improving', unit: 's' },
    { metric: 'Time to Interactive', value: 4.1, target: 5.0, trend: 'stable', unit: 's' },
    { metric: 'First Contentful Paint', value: 1.8, target: 2.5, trend: 'improving', unit: 's' },
    { metric: 'Cumulative Layout Shift', value: 0.08, target: 0.10, trend: 'stable', unit: '' },
    { metric: 'Error Rate', value: 0.2, target: 0.5, trend: 'improving', unit: '%' }
  ];

  // Generate conversion funnels
  data.funnels = [
    {
      name: 'User Onboarding',
      steps: [
        { name: 'Landing Page', users: 1000, conversionRate: 1.0 },
        { name: 'Sign Up Form', users: 650, conversionRate: 0.65 },
        { name: 'Email Verification', users: 520, conversionRate: 0.80 },
        { name: 'Profile Setup', users: 390, conversionRate: 0.75 },
        { name: 'First Action', users: 280, conversionRate: 0.72 }
      ]
    },
    {
      name: 'Report Generation',
      steps: [
        { name: 'Dashboard Access', users: 800, conversionRate: 1.0 },
        { name: 'Report Builder', users: 480, conversionRate: 0.60 },
        { name: 'Configuration', users: 360, conversionRate: 0.75 },
        { name: 'Generate Report', users: 270, conversionRate: 0.75 },
        { name: 'Download/Share', users: 215, conversionRate: 0.80 }
      ]
    }
  ];

  return data;
}

// Calculate engagement score based on multiple factors
function calculateEngagementScore(sessionDuration, pagesPerSession, bounceRate) {
  let score = 0;
  
  // Session duration score (40% weight)
  if (sessionDuration >= ENGAGEMENT_THRESHOLDS.session.excellent) score += 40;
  else if (sessionDuration >= ENGAGEMENT_THRESHOLDS.session.good) score += 30;
  else if (sessionDuration >= ENGAGEMENT_THRESHOLDS.session.average) score += 20;
  else score += 10;

  // Pages per session score (30% weight)
  if (pagesPerSession >= ENGAGEMENT_THRESHOLDS.pageViews.excellent) score += 30;
  else if (pagesPerSession >= ENGAGEMENT_THRESHOLDS.pageViews.good) score += 22;
  else if (pagesPerSession >= ENGAGEMENT_THRESHOLDS.pageViews.average) score += 15;
  else score += 8;

  // Bounce rate score (30% weight, inverted)
  if (bounceRate <= ENGAGEMENT_THRESHOLDS.bounceRate.excellent) score += 30;
  else if (bounceRate <= ENGAGEMENT_THRESHOLDS.bounceRate.good) score += 22;
  else if (bounceRate <= ENGAGEMENT_THRESHOLDS.bounceRate.average) score += 15;
  else score += 8;

  return Math.min(100, score);
}

function generateDropoffPoints() {
  return [
    { step: 'Landing', dropoff: 0.20 },
    { step: 'Navigation', dropoff: 0.15 },
    { step: 'Content View', dropoff: 0.25 },
    { step: 'Action Intent', dropoff: 0.40 }
  ];
}

function generateTopPages() {
  return [
    { page: '/dashboard', views: 2450, avgTime: 245 },
    { page: '/reports', views: 1880, avgTime: 320 },
    { page: '/analytics', views: 1650, avgTime: 180 },
    { page: '/settings', views: 890, avgTime: 420 }
  ];
}

// State management
function load() {
  try {
    const raw = localStorage.getItem(USER_ENGAGEMENT_KEY);
    return raw ? JSON.parse(raw) : generateEngagementData(30);
  } catch {
    return generateEngagementData(30);
  }
}

let state = load();
const subscribers = new Set();

function save() {
  try {
    localStorage.setItem(USER_ENGAGEMENT_KEY, JSON.stringify(state));
  } catch {}
}

function publish() {
  save();
  subscribers.forEach(cb => {
    try {
      cb(state);
    } catch {}
  });
}

// Public API
export function subscribe(callback) {
  subscribers.add(callback);
  callback(state);
  return () => subscribers.delete(callback);
}

export function getBehavioralData(options = {}) {
  const { dateFrom, dateTo, segment } = options;
  let data = [...state.behavioral];

  if (dateFrom) {
    data = data.filter(item => new Date(item.date) >= new Date(dateFrom));
  }
  if (dateTo) {
    data = data.filter(item => new Date(item.date) <= new Date(dateTo));
  }

  return data;
}

export function getUserJourneys() {
  return state.journeys || [];
}

export function getCohortAnalysis() {
  return state.cohorts || [];
}

export function getUserSegments() {
  return state.segments || [];
}

export function getABTests() {
  return state.abTests || [];
}

export function getPerformanceMetrics() {
  return state.performance || [];
}

export function getConversionFunnels() {
  return state.funnels || [];
}

export function getEngagementSummary(days = 7) {
  const recentData = state.behavioral.slice(-days);
  if (recentData.length === 0) return null;

  const totalUsers = recentData.reduce((sum, day) => sum + day.users, 0);
  const totalSessions = recentData.reduce((sum, day) => sum + day.sessions, 0);
  const totalPageViews = recentData.reduce((sum, day) => sum + day.pageViews, 0);
  const avgBounceRate = recentData.reduce((sum, day) => sum + day.bounceRate, 0) / recentData.length;
  const avgSessionDuration = recentData.reduce((sum, day) => sum + day.avgSessionDuration, 0) / recentData.length;
  const avgEngagementScore = recentData.reduce((sum, day) => sum + day.engagementScore, 0) / recentData.length;

  return {
    totalUsers,
    totalSessions,
    totalPageViews,
    avgBounceRate: Number(avgBounceRate.toFixed(3)),
    avgSessionDuration: Number(avgSessionDuration.toFixed(1)),
    avgEngagementScore: Number(avgEngagementScore.toFixed(1)),
    sessionsPerUser: Number((totalSessions / totalUsers).toFixed(2)),
    pageViewsPerSession: Number((totalPageViews / totalSessions).toFixed(2))
  };
}

export function getRealTimeMetrics() {
  // Simulate real-time data
  const activeUsers = 45 + Math.floor(Math.random() * 85);
  const activeSessions = Math.floor(activeUsers * 1.2);
  const pageViewsPerMinute = 15 + Math.floor(Math.random() * 25);
  
  return {
    activeUsers,
    activeSessions,
    pageViewsPerMinute,
    avgSessionDuration: 280 + Math.random() * 120,
    topPages: [
      { page: '/dashboard', activeUsers: Math.floor(activeUsers * 0.35) },
      { page: '/reports', activeUsers: Math.floor(activeUsers * 0.25) },
      { page: '/analytics', activeUsers: Math.floor(activeUsers * 0.20) },
      { page: '/settings', activeUsers: Math.floor(activeUsers * 0.20) }
    ]
  };
}

// Event tracking
export function trackEvent(eventName, properties = {}) {
  const event = {
    id: Date.now() + Math.random().toString(36).substr(2, 9),
    name: eventName,
    properties,
    timestamp: new Date().toISOString(),
    userId: properties.userId || 'anonymous',
    sessionId: properties.sessionId || 'session_' + Date.now()
  };

  // Store real-time events
  try {
    const realTimeEvents = JSON.parse(localStorage.getItem(REAL_TIME_EVENTS_KEY) || '[]');
    realTimeEvents.push(event);
    
    // Keep only last 100 events
    if (realTimeEvents.length > 100) {
      realTimeEvents.splice(0, realTimeEvents.length - 100);
    }
    
    localStorage.setItem(REAL_TIME_EVENTS_KEY, JSON.stringify(realTimeEvents));
  } catch {}

  return event;
}

export function getRealTimeEvents(limit=50){
  try {
    const list = JSON.parse(localStorage.getItem(REAL_TIME_EVENTS_KEY) || '[]');
    if(!Array.isArray(list)) return [];
    return list.slice(-limit).reverse();
  } catch { return []; }
}

// Data management
export function refreshData(days = 30) {
  state = generateEngagementData(days);
  publish();
}

export function exportData() {
  return {
    engagement: state,
    realTimeEvents: JSON.parse(localStorage.getItem(REAL_TIME_EVENTS_KEY) || '[]'),
    exportDate: new Date().toISOString()
  };
}

export function importData(data) {
  if (data && data.engagement) {
    state = data.engagement;
    if (data.realTimeEvents) {
      localStorage.setItem(REAL_TIME_EVENTS_KEY, JSON.stringify(data.realTimeEvents));
    }
    publish();
  }
}

// Utility functions for analytics
export function getEngagementThresholds() {
  return ENGAGEMENT_THRESHOLDS;
}

export function calculateRetentionRate(cohortData, week) {
  if (!cohortData || !cohortData[`week${week}`]) return 0;
  return (cohortData[`week${week}`] * 100).toFixed(1);
}

export function getEngagementLevel(score) {
  if (score >= 80) return { level: 'Excellent', color: '#28a745' };
  if (score >= 60) return { level: 'Good', color: '#17a2b8' };
  if (score >= 40) return { level: 'Average', color: '#ffc107' };
  return { level: 'Poor', color: '#dc3545' };
}